import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { LogIn, Mail, Users, Truck } from 'lucide-react';

export const LoginPage = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { t, language, isRTL } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const userData = await login(formData.email, formData.password);
      toast.success(t('welcomeBack') + '!');
      
      // توجيه السائقين / Super Admin / Dashboard العادية
      if (userData?.is_super_admin) {
        navigate('/super-admin');
      } else if (userData?.role === 'driver') {
        navigate('/driver');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      const detail = error?.response?.data?.detail;
      const msg = typeof detail === 'string' && detail ? detail : t('loginFailed');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleContactEmail = () => {
    window.location.href = 'mailto:albareq@dlr.iq?subject=طلب إنشاء حساب جديد&body=السلام عليكم،%0A%0Aأرغب في إنشاء حساب جديد في نظام شركة البريق.%0A%0Aالاسم:%0Aرقم الهاتف:%0A%0Aشكراً لكم';
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* الجهة اليمنى - اسم الشركة ونموذج الدخول */}
      <div className="flex-1 flex items-center justify-center bg-gradient-to-br from-primary via-[#1e3a5f] to-[#0f2744] p-8 lg:p-16 relative overflow-hidden">
        {/* خلفية زخرفية */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 right-20 w-64 h-64 bg-accent rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 left-20 w-96 h-96 bg-white rounded-full blur-3xl"></div>
        </div>
        
        <div className="w-full max-w-md relative z-10">
          {/* اسم الشركة */}
          <div className="text-center mb-10">
            <h1 className="text-5xl lg:text-6xl font-bold font-cairo text-white mb-4 drop-shadow-lg">
              {t('appName')}
            </h1>
            <p className="text-xl lg:text-2xl font-cairo text-accent">
              {t('appSubtitle')}
            </p>
            <div className="mt-4 w-32 h-1 bg-accent mx-auto rounded-full"></div>
          </div>

          {/* نموذج تسجيل الدخول */}
          <div className="bg-white/95 backdrop-blur-lg rounded-2xl shadow-2xl p-8" data-testid="login-card">
            <h2 className="text-2xl font-bold font-cairo text-primary text-center mb-2">
              {t('login')}
            </h2>
            <p className="text-slate-500 font-cairo text-center text-sm mb-6">
              {t('loginAccessDashboard')}
            </p>
            
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <Label htmlFor="email" className="font-cairo text-slate-700">{t('email')}</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="font-cairo h-12 text-base"
                  placeholder="example@email.com"
                  data-testid="email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="font-cairo text-slate-700">{t('password')}</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="font-cairo h-12 text-base"
                  placeholder="••••••••"
                  data-testid="password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full bg-primary hover:bg-secondary font-cairo h-12 text-lg"
                disabled={loading}
                data-testid="submit-btn"
              >
                {loading ? (
                  t('loading')
                ) : (
                  <>
                    <LogIn className={isRTL ? "ml-2" : "mr-2"} size={20} />
                    {t('login')}
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={handleContactEmail}
                className="text-sm text-primary hover:text-secondary font-cairo flex items-center justify-center gap-2 w-full transition-colors"
                data-testid="contact-email-btn"
              >
                <Mail size={16} />
                {t('noAccountContact')}
              </button>
            </div>

            {/* رابط بوابة العملاء */}
            <div className="mt-6 pt-6 border-t border-slate-200 space-y-3">
              <Link 
                to="/portal/login"
                className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-gradient-to-r from-primary/10 to-accent/10 hover:from-primary/20 hover:to-accent/20 border border-primary/20 rounded-xl text-primary font-cairo transition-all duration-300 group"
                data-testid="customer-portal-link"
              >
                <Users size={20} className="group-hover:scale-110 transition-transform text-accent" />
                <span className="font-semibold">بوابة العملاء</span>
                <span className="text-xs opacity-60 hidden sm:inline">| Customer Portal</span>
              </Link>
              
              {/* رابط بوابة السائقين */}
              <Link 
                to="/driver-login"
                className="flex items-center justify-center gap-2 w-full py-3 px-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 hover:from-amber-500/20 hover:to-orange-500/20 border border-amber-500/20 rounded-xl text-amber-700 font-cairo transition-all duration-300 group"
                data-testid="driver-portal-link"
              >
                <Truck size={20} className="group-hover:scale-110 transition-transform text-amber-600" />
                <span className="font-semibold">بوابة السائقين</span>
                <span className="text-xs opacity-60 hidden sm:inline">| Driver Portal</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* الجهة اليسرى - الشعار 3D */}
      <div className="hidden lg:flex flex-1 items-center justify-center bg-slate-900 relative overflow-hidden">
        {/* خلفية متحركة */}
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900"></div>
          {/* خطوط زخرفية */}
          <div className="absolute inset-0 opacity-20">
            {[...Array(5)].map((_, i) => (
              <div 
                key={`deco-${i}`}
                className="absolute h-px bg-gradient-to-r from-transparent via-accent to-transparent w-full"
                style={{ top: `${20 + i * 15}%`, animationDelay: `${i * 0.2}s` }}
              ></div>
            ))}
          </div>
        </div>
        
        {/* الشعار مع تأثير 3D */}
        <div className="relative z-10 transform-gpu perspective-1000">
          <div 
            className="relative animate-float"
            style={{
              animation: 'float 6s ease-in-out infinite',
              transformStyle: 'preserve-3d'
            }}
          >
            {/* ظل الشعار */}
            <div 
              className="absolute inset-0 blur-2xl opacity-50"
              style={{ transform: 'translateZ(-50px) scale(1.1)' }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_design-agency-erp/artifacts/0u8gy12u_%D9%84%D9%88%D9%83%D9%88%20%D8%B4%D8%B1%D9%83%D8%A9%20%D8%A8%D8%B1%D9%8A%D9%82.png"
                alt=""
                className="w-80 h-80 object-contain"
              />
            </div>
            
            {/* الشعار الرئيسي */}
            <img
              src="https://customer-assets.emergentagent.com/job_design-agency-erp/artifacts/0u8gy12u_%D9%84%D9%88%D9%83%D9%88%20%D8%B4%D8%B1%D9%83%D8%A9%20%D8%A8%D8%B1%D9%8A%D9%82.png"
              alt="Logo"
              className="w-80 h-80 object-contain drop-shadow-2xl relative z-10"
              style={{
                filter: 'drop-shadow(0 20px 40px rgba(212, 175, 55, 0.3))',
                transform: 'rotateY(-5deg) rotateX(5deg)'
              }}
            />
          </div>
          
          {/* نص تحت الشعار */}
          <div className="text-center mt-8">
            <p className="text-accent font-cairo text-xl tracking-widest">
              AL-BARIQ
            </p>
            <p className="text-slate-500 font-cairo text-sm mt-2">
              Design & Advertising
            </p>
          </div>
        </div>
      </div>

      {/* CSS للحركة */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0) rotateY(-5deg) rotateX(5deg); }
          50% { transform: translateY(-20px) rotateY(5deg) rotateX(-5deg); }
        }
        .animate-float {
          animation: float 6s ease-in-out infinite;
        }
        .perspective-1000 {
          perspective: 1000px;
        }
      `}</style>
    </div>
  );
};